import { ZonedDate } from './zonedDate';

/**
 * A site's wall-clock time, `YYYY-MM-DDTHH:mm:ss` with no zone: what the bioscope's own clock
 * read. The behaviour and environment readings are stored that way, so the value names no
 * instant and is shown as it is, in every browser zone.
 *
 * Older backends sent the same digits with a `Z`, because the driver read the zone-less column
 * as UTC. The digits are the wall clock either way, so a trailing `Z` (and a fraction of a
 * second) is accepted and ignored. A string with any other offset is an instant, not a wall
 * clock, and is rejected.
 */
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:[.,]\d+)?)?Z?$/;

/** The wall clock's fields as UTC-equivalent milliseconds, or NaN when it is not a real time. */
function wallClockMs(value: string | null | undefined): number {
    const match = value ? WALL_CLOCK.exec(value) : null;
    if (!match) return Number.NaN;

    const [, year, month, day, hour, minute, second] = match;
    const parts = [
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second ?? 0),
    ] as const;
    const ms = Date.UTC(...parts);
    const check = new Date(ms);
    const rolledOver =
        check.getUTCFullYear() !== parts[0] ||
        check.getUTCMonth() !== parts[1] ||
        check.getUTCDate() !== parts[2] ||
        check.getUTCHours() !== parts[3] ||
        check.getUTCMinutes() !== parts[4] ||
        check.getUTCSeconds() !== parts[5];
    return rolledOver ? Number.NaN : ms;
}

/**
 * The wall clock as a date whose getters, and so date-fns formatting, read its own fields, or
 * null when it is missing or not a wall clock. It is read in UTC, which has no DST switch, so
 * a time the browser's own zone skips (02:30 on the night Oslo moves its clocks forward) is
 * kept as it is.
 */
export function wallClockDate(value: string | null | undefined): Date | null {
    const ms = wallClockMs(value);
    return Number.isNaN(ms) ? null : new ZonedDate(ms, 'UTC');
}

/**
 * The API's wall-clock value as `YYYY-MM-DDTHH:mm:ss`, or '' when it is missing or not a wall
 * clock. A legacy `...Z` value comes back without the `Z`.
 *
 * @example
 * toWallClock('2025-10-05T14:30:45'); // "2025-10-05T14:30:45"
 * toWallClock('2025-10-05T14:30:45.000Z'); // "2025-10-05T14:30:45"
 * toWallClock(''); // ""
 */
export function toWallClock(value: string | null | undefined): string {
    const ms = wallClockMs(value);
    return Number.isNaN(ms) ? '' : new Date(ms).toISOString().slice(0, 19);
}
